export type AnalyticsMode = {
  name: string;
  header: string;
  type: "cat" | "subcat";
  text: string;
  parent?: boolean;
  prefix?: string;
  negative?: boolean;
  currency?: boolean;
  fraction?: string;
  num?: number;
  sb_color?: string;
};

export type AnalyticsSidebarProps = {
  modes: AnalyticsMode[];
  activeMode: string;
  onSelect: (name: string) => void;
};

export function AnalyticsSidebar({
  modes,
  activeMode,
  onSelect,
}: AnalyticsSidebarProps) {
  return (
    <div className="ne__analytics__sidebar ne__analytics__sidebar--large">
      {modes.map((m, i) => (
        <a
          key={`${m.name}-${m.type}-${i}`}
          className={`ne__analytics__sidebar__item ${
            m.type === "subcat"
              ? "ne__analytics__sidebar__item--subsection"
              : "ne__analytics__sidebar__item--section"
          } ${
            m.name === activeMode ||
            (m.parent && activeMode.startsWith(m.prefix || "___"))
              ? "active"
              : ""
          }`}
          href={`#${m.name}`}
          onClick={(e) => {
            e.preventDefault();
            onSelect(m.name);
          }}
        >
          {m.text}
          {m.type !== "cat" || !m.parent ? (
            m.num != null ? (
              <span
                className={`ne__analytics__sidebar__stats ${m.sb_color || ""}`}
              >
                <span className="ne__analytics__sidebar__caret">
                  {m.num >= 0 ? "▲" : "▼"}
                </span>
                <span className="ne__analytics__sidebar__number">
                  {m.currency
                    ? Math.abs(m.num).toLocaleString(undefined, {
                        style: "currency",
                        currency: "USD",
                        maximumFractionDigits: 0,
                      })
                    : Math.abs(m.num)}
                </span>
              </span>
            ) : null
          ) : null}
        </a>
      ))}
    </div>
  );
}

export type BarGraphProps = {
  amounts: Array<[number | string, number]>;
  max: number;
  currency?: boolean;
  fraction?: string;
};

/**
 * Historical CSS 3D bar graph (`ne__bar-graph`).
 * Heights animate via CSS transitions on height/bottom (see index.css).
 * Cap at 78% so top faces never leave the chart box / viewport.
 * Bars keep stable keys so mode/unit changes tween between values.
 * On series length change we briefly zero heights so grow-in re-triggers.
 */
export function BarGraph({ amounts, max, currency, fraction }: BarGraphProps) {
  const safeMax = max > 0 ? max : 1;
  const n = amounts.length;
  const barsClass =
    n <= 4 ? "bars4" : n <= 7 ? "bars7" : n <= 12 ? "bars12" : "bars12";

  // Render final heights immediately. The zero→target CSS grow-in was pretty
  // for humans but left pixelmatch residual when capture landed mid-tween
  // (and VRT already kills transitions via stabilize CSS).
  const live = amounts;

  return (
    <div className="ne__bar-graph">
      <div className={`ne__bar-graph__bar-group ${barsClass}`}>
        {live.map(([label, value], index) => {
          const source = amounts[index];
          const displayValue = source?.[1] ?? value;
          /* Angular front faces measure ~value/max of group height (no 92% cap) */
          const height = `${Math.max(0, (Number(value) / safeMax) * 100)}%`;
          return (
            <div
              key={`bar-col-${index}`}
              className={`ne__bar-graph__bar bar-60-${index + 1}`}
            >
              <div className="side topside" style={{ bottom: height }} />
              <div className="side leftside" style={{ height }} />
              <div className="side frontside" style={{ height }} />
              <div className="side rightside" style={{ height }} />
              <div className="side shadowbase" style={{ height }} />
              <div className="side metatext ne__bar-graph__legend">
                <div className="ne__bar-graph__legend__title">{label}</div>
                <div className="ne__bar-graph__legend__number">
                  <span>
                    {currency
                      ? Number(displayValue).toLocaleString(undefined, {
                          style: "currency",
                          currency: "USD",
                          maximumFractionDigits: 0,
                        })
                      : fraction
                        ? Number(displayValue).toFixed(2)
                        : displayValue}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export type AnalyticsStatsProps = {
  value: number | string | undefined;
  header: string;
  description?: string;
  color?: "red" | "green" | "gray";
  currency?: boolean;
  fraction?: string;
};

export function AnalyticsStats({
  value,
  header,
  description,
  color,
  currency,
  fraction,
}: AnalyticsStatsProps) {
  const display =
    value == null
      ? "—"
      : currency
        ? Number(value).toLocaleString(undefined, {
            style: "currency",
            currency: "USD",
            maximumFractionDigits: 0,
          })
        : fraction
          ? Number(value).toFixed(2)
          : value;

  return (
    <div className="ne__analytics__stats">
      <span className="ne__analytics__stats__number">
        <span className="ne__analytics__stats__integer">{display}</span>
      </span>
      <span className="ne__analytics__stats__header">{header}</span>
      {description ? (
        <div
          className={`ne__analytics__stats__description ${color || ""}`}
        >
          {description}
        </div>
      ) : null}
    </div>
  );
}
